import { isAfter, isBefore } from 'date-fns';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'react-i18next';
import PageEndMarker from '~/components/PageEndMarker';
import { SkeletonLoader } from '~/components/data-display/SkeletonLoader/SkeletonLoader';
import Text from '~/components/typographies/Text';
import { useDocumentInfiniteQuery } from '~/services/document';
import {
  reconcileTransactionsFilterScopeValue,
  reconcileTransactionsFiltersStorageKey,
  useReconcileTransactionsFilters,
  useShowReconciledFlows,
} from '~/state/modals/reconcileTransactionModal';
import { TableFiltersProvider } from '~/state/tableFilters';
import { DocumentStatus, IssuableDocumentTypes } from '~/types/document';
import { Flow, FlowPaymentStatus, FlowWithDocument } from '~/types/flow';
import { ReconciliationRelationStatus } from '~/types/reconciliation';
import styles from './ReconcileSearch.module.scss';
import ReconcileSearchFilters from './ReconcileSearchFilters';
import SearchResultCard from './SearchResultCard';

// Draft documents must not appear in the reconciliation search unless the user
// explicitly filters for them (see the "Draft" option in FlowPaymentStatusFilter).
const NonDraftDocumentStatuses = Object.values(DocumentStatus).filter((status) => status !== DocumentStatus.Draft);

function insideExpectedPaymentDateRange(flow: Flow, [start, end]: [string | null, string | null]) {
  const afterStart = start ? !isAfter(start, flow.expectedPaymentDate) : true;
  const beforeEnd = end ? !isBefore(end, flow.expectedPaymentDate) : true;
  return afterStart && beforeEnd;
}

export const useDocumentsSearch = () => {
  const filters = useReconcileTransactionsFilters();
  const [showReconciled] = useShowReconciledFlows();
  const isDraftFilter = filters.flowPaymentStatus === DocumentStatus.Draft;
  const flowPaymentStatus = isDraftFilter ? null : (filters.flowPaymentStatus as FlowPaymentStatus | null);
  const paginationController = useDocumentInfiniteQuery({
    search: filters.search,
    totalAmount: filters.totalAmount,
    creationDate: filters.documentDateRange,
    documentTypes: filters.documentType.length > 0 ? filters.documentType : IssuableDocumentTypes,
    currencies: [],
    includeHidden: false,
    paymentStatus: null,
    flowHasPaymentStatus: flowPaymentStatus,
    direction: null,
    status: isDraftFilter ? [DocumentStatus.Draft] : NonDraftDocumentStatuses,
    flowHasPaymentMethod: filters.flowPaymentMethod,
    flowHasExpectedPaymentDate: filters.flowPaymentDate,
    counterpart: filters.counterpartId ? [filters.counterpartId] : undefined,
  });

  return {
    paginationController,
    isSearching: paginationController.isFetching && !paginationController.data,
    results: (paginationController.data?.pages.flatMap(({ documents }) => documents) ?? [])
      .filter(
        (document) =>
          showReconciled ||
          document.flows
            ?.map((f) => f.reconciliations?.filter((r) => r.status == ReconciliationRelationStatus.VERIFIED) || [])
            .filter((reconciliations) => reconciliations.length === 0).length !== 0,
      )
      .map((document) => {
        const flows = (document.flows || [])

          .filter((f) => insideExpectedPaymentDateRange(f, filters.flowPaymentDate))
          .filter(
            (f) => filters.flowPaymentStatus === null || isDraftFilter || f.paymentStatus === filters.flowPaymentStatus,
          )
          .filter(
            (f) =>
              filters.flowPaymentMethod.length === 0 ||
              (f.paymentMethod && filters.flowPaymentMethod.includes(f.paymentMethod)),
          )
          .map((f) => {
            f.document = document;
            return f;
          }) as FlowWithDocument[];

        return { document, flows: sortBy(flows, (f) => f.paymentDate) };
      })
      .filter((x) => x.flows.length > 0),
  };
};

const SearchResults = () => {
  const { t } = useTranslation('reconciliations');
  const { isSearching, paginationController, results } = useDocumentsSearch();

  if (isSearching) {
    return <SkeletonLoader height={100} />;
  }

  if (results.length === 0) {
    return (
      <Text variant="body2" weight="regular">
        {t('nothing_found')}
      </Text>
    );
  }

  return (
    <div className={styles.searchResults}>
      {results.map((result) => (
        <SearchResultCard key={result.document.id} document={result.document} flows={result.flows} />
      ))}
      <PageEndMarker height={72} query={paginationController} />
    </div>
  );
};

export const ReconcileSearch = () => {
  return (
    <TableFiltersProvider
      {...reconcileTransactionsFilterScopeValue}
      storageKey={reconcileTransactionsFiltersStorageKey}
    >
      <div className={styles.root}>
        <ReconcileSearchFilters />
        <SearchResults />
      </div>
    </TableFiltersProvider>
  );
};
